"use client";
import { taskStore, TaskItem } from "@/lib/store";
import { useStore } from "@/lib/useStore";
import { useState } from "react";
import { Plus, X, GripVertical } from "lucide-react";

type Status = TaskItem["status"];
type Priority = TaskItem["priority"];

const COLUMNS: { key: Status; label: string; color: string }[] = [
  { key: "todo", label: "📋 To Do", color: "#eab308" },
  { key: "inprogress", label: "🔄 In Progress", color: "#3b82f6" },
  { key: "done", label: "✅ Done", color: "#22c55e" },
];

const PRIORITY_COLORS: Record<Priority, string> = { low: "#22c55e", med: "#eab308", high: "#ef4444" };

export function TasksBoard() {
  const items = useStore(taskStore.list);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<TaskItem | null>(null);
  const [dragItem, setDragItem] = useState<string | null>(null);

  const [form, setForm] = useState({ title: "", description: "", assignedTo: "Karl", priority: "med" as Priority, dueDate: "", status: "todo" as Status });

  const resetForm = () => { setForm({ title: "", description: "", assignedTo: "Karl", priority: "med", dueDate: "", status: "todo" }); setShowCreate(false); };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    await taskStore.create({ ...form, dueDate: form.dueDate || undefined, createdBy: "Karl" });
    resetForm();
  };

  const handleUpdate = async () => {
    if (!editing) return;
    await taskStore.update(editing.id, { title: editing.title, description: editing.description, status: editing.status, assignedTo: editing.assignedTo, priority: editing.priority, dueDate: editing.dueDate });
    setEditing(null);
  };

  const handleDrop = async (status: Status) => { if (!dragItem) return; await taskStore.update(dragItem, { status }); setDragItem(null); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Tasks Board</h2>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] rounded-lg text-sm font-medium transition-colors">
          <Plus size={16} /> New Task
        </button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const colItems = items.filter((i) => i.status === col.key).sort((a, b) => a.order - b.order);
          return (
            <div key={col.key} className="min-w-[320px] w-[320px] shrink-0 bg-[var(--bg-column)] rounded-xl border border-[var(--border)]" onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop(col.key)}>
              <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
                <span className="text-sm font-semibold">{col.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: col.color + "22", color: col.color }}>{colItems.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {colItems.map((item) => (
                  <div key={item.id} draggable onDragStart={() => setDragItem(item.id)} onClick={() => setEditing({ ...item })} className="bg-[var(--bg-card)] rounded-lg p-3 border border-[var(--border)] hover:border-[var(--accent)] cursor-pointer transition-colors group">
                    <div className="flex items-start gap-2">
                      <GripVertical size={14} className="mt-0.5 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 shrink-0 cursor-grab" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PRIORITY_COLORS[item.priority] }} />
                          <p className="text-sm font-medium truncate">{item.title}</p>
                        </div>
                        {item.description && <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">{item.description}</p>}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-[var(--text-muted)]">{item.assignedTo === "Casey" ? "🐕" : "👤"} {item.assignedTo}</span>
                          {item.dueDate && <span className="text-[10px] text-[var(--text-muted)]">📅 {item.dueDate}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <Modal onClose={resetForm} title="New Task">
          <Input placeholder="Task title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Textarea placeholder="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={4} />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><Label>Assign to</Label><Select value={form.assignedTo} onChange={(v) => setForm({ ...form, assignedTo: v })} options={[{ value: "Karl", label: "👤 Karl" }, { value: "Casey", label: "🐕 Casey" }]} /></div>
            <div><Label>Priority</Label><Select value={form.priority} onChange={(v) => setForm({ ...form, priority: v as Priority })} options={[{ value: "low", label: "🟢 Low" }, { value: "med", label: "🟡 Medium" }, { value: "high", label: "🔴 High" }]} /></div>
          </div>
          <div className="mt-3"><Label>Due date</Label><input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]" /></div>
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={resetForm}>Cancel</Btn>
            <Btn onClick={handleCreate}>Create</Btn>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit Task">
          <Input value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <Textarea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={4} />
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div><Label>Status</Label><Select value={editing.status} onChange={(v) => setEditing({ ...editing, status: v as Status })} options={COLUMNS.map((c) => ({ value: c.key, label: c.label }))} /></div>
            <div><Label>Assign to</Label><Select value={editing.assignedTo} onChange={(v) => setEditing({ ...editing, assignedTo: v })} options={[{ value: "Karl", label: "👤 Karl" }, { value: "Casey", label: "🐕 Casey" }]} /></div>
            <div><Label>Priority</Label><Select value={editing.priority} onChange={(v) => setEditing({ ...editing, priority: v as Priority })} options={[{ value: "low", label: "🟢 Low" }, { value: "med", label: "🟡 Med" }, { value: "high", label: "🔴 High" }]} /></div>
          </div>
          <div className="mt-3"><Label>Due date</Label><input type="date" value={editing.dueDate || ""} onChange={(e) => setEditing({ ...editing, dueDate: e.target.value })} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]" /></div>
          <div className="flex justify-between mt-4">
            <Btn variant="danger" onClick={async () => { await taskStore.remove(editing.id); setEditing(null); }}>Delete</Btn>
            <div className="flex gap-2"><Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn><Btn onClick={handleUpdate}>Save</Btn></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return <input placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm mt-2 focus:outline-none focus:border-[var(--accent)]" />;
}
function Textarea({ value, onChange, placeholder, rows }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return <textarea placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm mt-2 resize-none focus:outline-none focus:border-[var(--accent)]" />;
}
function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]">{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
}
function Label({ children }: { children: React.ReactNode }) { return <p className="text-xs text-[var(--text-muted)] mb-1">{children}</p>; }
function Btn({ children, onClick, variant }: { children: React.ReactNode; onClick: () => void; variant?: "ghost" | "danger" }) {
  const cls = variant === "ghost" ? "text-[var(--text-muted)] hover:text-[var(--text)]" : variant === "danger" ? "text-[var(--red)] hover:bg-[var(--red)]/10" : "bg-[var(--accent)] hover:bg-[var(--accent-hover)] font-medium";
  return <button onClick={onClick} className={`px-4 py-2 text-sm rounded-lg transition-colors ${cls}`}>{children}</button>;
}
